import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useDateiDrop } from '../lib/useDateiDrop';
import './ImportWizard.css';

const CSV_ACCEPT = '.csv,.xlsx,.xls';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  authToken: string;
}

type Schritt = 'upload' | 'mapping' | 'ergebnis';

type ImportTyp = 'artikel' | 'partner' | 'preisliste';

const TYP_OPTIONEN: { value: ImportTyp; label: string }[] = [
  { value: 'artikel', label: 'Artikel' },
  { value: 'partner', label: 'Partner (Kunden/Lieferanten)' },
  { value: 'preisliste', label: 'Preisliste' },
];

const DB_FELDER: Record<ImportTyp, { value: string; label: string }[]> = {
  artikel: [
    { value: '', label: '— ignorieren —' },
    { value: 'artikel_nr', label: 'Artikel-Nr.' },
    { value: 'bezeichnung', label: 'Bezeichnung' },
    { value: 'einheit', label: 'Einheit' },
    { value: 'verkaufspreis', label: 'Verkaufspreis' },
    { value: 'einkaufspreis', label: 'Einkaufspreis' },
    { value: 'kategorie', label: 'Kategorie' },
  ],
  partner: [
    { value: '', label: '— ignorieren —' },
    { value: 'name', label: 'Name' },
    { value: 'typ', label: 'Typ (Kunde/Lieferant)' },
    { value: 'email', label: 'E-Mail' },
    { value: 'telefon', label: 'Telefon' },
    { value: 'adresse', label: 'Adresse' },
  ],
  preisliste: [
    { value: '', label: '— ignorieren —' },
    { value: 'bezeichnung', label: 'Artikel-Bezeichnung' },
    { value: 'preis', label: 'Preis' },
  ],
};

interface PreviewData {
  spalten: string[];
  vorschau: string[][];
  alle_zeilen: string[][];
  mapping_vorschlag: Record<string, string>;
}

interface ImportErgebnis {
  erstellt: number;
  aktualisiert: number;
  fehler: { zeile: number; fehler: string }[];
}

const ImportWizard: React.FC<Props> = ({ authToken }) => {
  const [schritt, setSchritt] = useState<Schritt>('upload');
  const [typ, setTyp] = useState<ImportTyp>('artikel');
  const [datei, setDatei] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // ── Drag-and-Drop für die CSV/Excel-Datei ──
  const { dragOver: csvDragOver, dropHandlers: csvDropHandlers } = useDateiDrop({
    accept: CSV_ACCEPT,
    onFiles: dateien => setDatei(dateien[0]),
    onError: fehler => setErrorMsg(fehler),
  });

  // Preview
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Alle Zeilen vom Server (für Commit)
  const [alleZeilen, setAlleZeilen] = useState<string[][]>([]);
  const [ergebnis, setErgebnis] = useState<ImportErgebnis | null>(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  // -----------------------------------------------------------------------
  // Schritt 1: Upload + Preview
  // -----------------------------------------------------------------------
  const handleUpload = async () => {
    if (!datei) {
      setErrorMsg('Bitte wähle eine Datei aus.');
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append('datei', datei);

      const res = await axios.post<PreviewData>(
        `${API_URL}/api/import/preview?typ=${typ}`,
        formData,
        { headers },
      );

      setPreview(res.data);
      setMapping(res.data.mapping_vorschlag);
      setAlleZeilen(res.data.alle_zeilen);

      setSchritt('mapping');
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.detail ?? err.message
        : String(err);
      setErrorMsg(`Fehler beim Laden der Vorschau: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Schritt 2: Mapping anpassen
  // -----------------------------------------------------------------------
  const handleMappingChange = (csvSpalte: string, dbFeld: string) => {
    setMapping(prev => {
      const neu = { ...prev };
      if (dbFeld === '') {
        delete neu[csvSpalte];
      } else {
        neu[csvSpalte] = dbFeld;
      }
      return neu;
    });
  };

  // -----------------------------------------------------------------------
  // Schritt 3: Commit
  // -----------------------------------------------------------------------
  const handleCommit = async () => {
    if (!preview) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const payload = {
        typ,
        spalten: preview.spalten,
        mapping,
        zeilen: alleZeilen,
      };
      const res = await axios.post<ImportErgebnis>(
        `${API_URL}/api/import/commit`,
        payload,
        { headers },
      );
      setErgebnis(res.data);
      setSchritt('ergebnis');
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.detail ?? err.message
        : String(err);
      setErrorMsg(`Import fehlgeschlagen: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Reset
  // -----------------------------------------------------------------------
  const handleNeustart = () => {
    setSchritt('upload');
    setDatei(null);
    setPreview(null);
    setMapping({});
    setAlleZeilen([]);
    setErgebnis(null);
    setErrorMsg(null);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="import-wizard">
      <div className="import-wizard__header">
        <h2>CSV / Excel Import</h2>
        <div className="import-wizard__steps">
          <span className={`import-step ${schritt === 'upload' ? 'import-step--active' : 'import-step--done'}`}>
            1 Datei
          </span>
          <span className="import-step__sep">›</span>
          <span className={`import-step ${schritt === 'mapping' ? 'import-step--active' : schritt === 'ergebnis' ? 'import-step--done' : ''}`}>
            2 Spalten
          </span>
          <span className="import-step__sep">›</span>
          <span className={`import-step ${schritt === 'ergebnis' ? 'import-step--active' : ''}`}>
            3 Ergebnis
          </span>
        </div>
      </div>

      <div className="import-wizard__info">
        Allgemeiner CSV/Excel-Import für Stammdaten (Artikel, Kontakte, Preislisten). Für Großhandels-Stücklisten den Tab 'Stücklisten' verwenden, für komplette Händler-Kataloge den Tab 'Kataloge'.
      </div>

      {errorMsg && (
        <div className="import-error">{errorMsg}</div>
      )}

      {/* Schritt 1: Upload */}
      {schritt === 'upload' && (
        <div className="import-section">
          <div className="import-field">
            <label>Importtyp</label>
            <select value={typ} onChange={e => setTyp(e.target.value as ImportTyp)}>
              {TYP_OPTIONEN.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="import-field">
            <label>Datei (CSV oder Excel .xlsx)</label>
            <div
              className={`import-dropzone ${csvDragOver ? 'import-dropzone--drag-over' : ''}`}
              onClick={() => csvInputRef.current?.click()}
              {...csvDropHandlers}
            >
              <span className="import-dropzone__text">
                {datei ? datei.name : 'CSV hierher ziehen oder klicken'}
              </span>
            </div>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={e => setDatei(e.target.files?.[0] ?? null)}
              style={{ display: 'none' }}
            />
          </div>

          <button
            className="import-btn import-btn--primary"
            onClick={handleUpload}
            disabled={loading || !datei}
          >
            {loading ? 'Lade Vorschau…' : 'Vorschau laden'}
          </button>
        </div>
      )}

      {/* Schritt 2: Mapping + Vorschau */}
      {schritt === 'mapping' && preview && (
        <div className="import-section">
          <h3>Spalten zuordnen</h3>
          <p className="import-hint">
            Weise jede CSV-Spalte einem Datenbankfeld zu. Nicht benötigte Spalten auf "ignorieren" lassen.
          </p>

          <div className="import-mapping-grid">
            {preview.spalten.map(spalte => (
              <div key={spalte} className="import-mapping-row">
                <span className="import-mapping-csv">{spalte}</span>
                <span className="import-mapping-arrow">→</span>
                <select
                  value={mapping[spalte] ?? ''}
                  onChange={e => handleMappingChange(spalte, e.target.value)}
                >
                  {DB_FELDER[typ].map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <h3 className="import-preview-title">Vorschau (erste 10 Zeilen)</h3>
          <div className="import-table-wrap">
            <table className="import-table">
              <thead>
                <tr>
                  {preview.spalten.map(s => <th key={s}>{s}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.vorschau.map((zeile, i) => (
                  <tr key={i}>
                    {zeile.map((zelle, j) => <td key={j}>{zelle}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="import-actions">
            <button className="import-btn" onClick={() => setSchritt('upload')}>
              Zurück
            </button>
            <button
              className="import-btn import-btn--primary"
              onClick={handleCommit}
              disabled={loading}
            >
              {loading ? 'Importiere…' : `Import starten (${alleZeilen.length} Zeilen)`}
            </button>
          </div>
        </div>
      )}

      {/* Schritt 3: Ergebnis */}
      {schritt === 'ergebnis' && ergebnis && (
        <div className="import-section">
          <h3>Import abgeschlossen</h3>

          <div className="import-stats">
            <div className="import-stat import-stat--ok">
              <span className="import-stat__zahl">{ergebnis.erstellt}</span>
              <span className="import-stat__label">erstellt</span>
            </div>
            <div className="import-stat import-stat--update">
              <span className="import-stat__zahl">{ergebnis.aktualisiert}</span>
              <span className="import-stat__label">aktualisiert</span>
            </div>
            <div className="import-stat import-stat--err">
              <span className="import-stat__zahl">{ergebnis.fehler.length}</span>
              <span className="import-stat__label">Fehler</span>
            </div>
          </div>

          {ergebnis.fehler.length > 0 && (
            <div className="import-fehler-liste">
              <h4>Fehlerhafte Zeilen</h4>
              <table className="import-table">
                <thead>
                  <tr>
                    <th>Zeile</th>
                    <th>Fehlermeldung</th>
                  </tr>
                </thead>
                <tbody>
                  {ergebnis.fehler.map((f, i) => (
                    <tr key={i}>
                      <td>{f.zeile}</td>
                      <td>{f.fehler}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button className="import-btn import-btn--primary" onClick={handleNeustart}>
            Neuer Import
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportWizard;
